import CollapseElem from 'Components/Collapse';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryTabs from 'Components/HistoryTabs';
import FormEditingHandler from 'Containers/FormEditingHandler';
import StandAloneFormEditingHandler from 'Containers/StandAloneFormEditingHandler';
import { sortPartialDate } from 'neuro-utils';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { actions } from 'Store/documents/actions';
import { useAppSelector as useSelector } from 'Store/index';
import { actions as myAppActions } from 'Store/myapp/actions';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';
import { myBackgroundDocumentTypes } from '../utils';
import CareTableHistory from './CareTableHistory';
import BackgroundFormDynamic from './FormDynamic';
import MeasurementFormMultiple from './FormMeasurement/multiple';
import HistoryRowData from './HistoryRowDataNew';

import type { DocTypesByPageName } from '../../../config/platformSettings';
import type { TBackgroundDocument, TMyBackgroundDocument } from '../utils';
import type { INewMeasurements } from './FormMeasurement/multiple';
import type { TCreationPromiseType } from 'Store/documents/actions';
import type { TDispatch } from 'Store/index';

const documentType = (documents: IDocumentV2[], id: string): TDocumentType | undefined =>
  documents.find(d => d._id === id)?._type;

const saveNewMeasurementDocs = (newMeasurements: INewMeasurements, dispatch: TDispatch) => {
  const creationPromises: Promise<TCreationPromiseType>[] = [];
  if (newMeasurements.date) {
    newMeasurements.values?.forEach(value => {
      if (!value.code || !value.value) return;
      const measurementData = {
        date: newMeasurements.date,
        time: newMeasurements.time,
        code: value.code,
        value: value.value,
      };
      creationPromises.push(actions.putDocument('measurement', measurementData)(dispatch));
    });
  }
  return Promise.all(creationPromises);
};

const Background = ({ documents }: IOwnProps): React.JSX.Element => {
  const [openTab, setOpenTab] = React.useState<number>(0);

  const users = useSelector(state => state.session.orgUsers) ?? [];

  // Background documents recorded by the patient in the mobile app
  const myAppDocuments = useSelector((s: IState) => s.myapp.sortedAndMergedDocuments);
  const myBgDocs = filterDocumentsByType(myAppDocuments, myBackgroundDocumentTypes);

  const backgroundDocuments: TBackgroundDocument[] = [...filterByDocumentType(documents, 'background'), ...myBgDocs];

  const careTableDocuments = filterByDocumentType(documents, 'measurement');
  const myCareTableDocs = myBgDocs.filter(d => 'height' in d || 'weight' in d || 'bmi' in d);

  const sortedBackgroundDocuments = backgroundDocuments
    .toSorted((n1, n2) => n2._cdate - n1._cdate)
    .sort((n1, n2) => sortPartialDate(n2.date, n1.date));

  const platform = useSelector((s: IState) => s.session.platforms?.selected);

  // My document handling
  const [sharedBgFormOpen, setSharedBgFormOpen] = React.useState<
    { doc: TMyBackgroundDocument; type: 'edit' | 'view' } | undefined
  >(undefined);
  const [tempSharedBg, setTempSharedBg] = React.useState<TMyBackgroundDocument | undefined>(undefined);
  const [saving, setSaving] = React.useState<boolean>(false);

  const dispatch = useDispatch();

  const saveSharedBg = async () => {
    if (!sharedBgFormOpen) return;
    setSaving(true);
    await myAppActions.putNewCommit(sharedBgFormOpen.doc._type, sharedBgFormOpen.doc._id, tempSharedBg, dispatch);
    setSaving(false);
    setSharedBgFormOpen(undefined);
  };

  const handleSharedBgFormChange = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (value || value === 0) {
      setTempSharedBg(prev => (prev ? { ...prev, [name]: value } : prev));
    }
  };

  const handleMyBackgroundDelete = async (myBackgroundId: string) => {
    const docType = myBgDocs.find(d => d._id === myBackgroundId)?._type;
    if (!docType) return;
    await myAppActions.deleteDocument(docType, myBackgroundId, dispatch);
  };

  React.useEffect(() => {
    if (sharedBgFormOpen) {
      setTempSharedBg(structuredClone(sharedBgFormOpen.doc));
    }
  }, [sharedBgFormOpen]);

  // Patient-recorded documents are the only ones in this view that are not of type 'background'.
  // A document without _type is one that is being created, which is never patient-recorded.
  const isBackgroundDocumentShared = (d: TBackgroundDocument | Record<string, never>): d is TMyBackgroundDocument =>
    '_type' in d && d._type !== 'background';

  // New measurements data
  const [measurementNewData, setMeasurementNewData] = React.useState<INewMeasurements | null>(null);
  const measurementNewDataOnChange = (values: TOnChangeValues) => {
    setMeasurementNewData({ ...measurementNewData, ...values });
  };

  return (
    <React.Fragment>
      {(openTab === 0 && measurementNewData) || (openTab === 1 && sharedBgFormOpen) ? (
        <StandAloneFormEditingHandler
          currentPageName="background"
          headerLocaleId={measurementNewData ? 'background.addCareData' : 'background.title'}
          loading={saving}
          cancelEditingAction={() => {
            if (measurementNewData) {
              setMeasurementNewData(null);
            }
            if (sharedBgFormOpen) {
              setSharedBgFormOpen(undefined);
            }
          }}
          saveAction={() => {
            setSaving(true);
            if (measurementNewData) {
              saveNewMeasurementDocs(measurementNewData, dispatch).then(() => {
                setSaving(false);
                setMeasurementNewData(null);
              });
            }
            if (sharedBgFormOpen) {
              saveSharedBg();
            }
          }}
          viewing={sharedBgFormOpen?.type === 'view'}
          endViewingAction={() => setSharedBgFormOpen(undefined)}
        >
          <>
            {measurementNewData && (
              <MeasurementFormMultiple
                formData={{ onChange: measurementNewDataOnChange, document: measurementNewData }}
              />
            )}
            {sharedBgFormOpen && (
              <BackgroundFormDynamic
                formData={{
                  onChange: handleSharedBgFormChange,
                  document: tempSharedBg ?? {},
                }}
                documents={backgroundDocuments}
                users={users}
                isShared
              />
            )}
          </>
        </StandAloneFormEditingHandler>
      ) : (
        <FormEditingHandler name="background" documents={documents}>
          {(editing, startEdit, formData): React.JSX.Element => {
            // Patient-recorded documents are edited through the standalone form
            const startEditFunction: StartEdit<TBackgroundDocument> = (document, name, initialFormData) => {
              if (isBackgroundDocumentShared(document)) {
                return () => setSharedBgFormOpen({ doc: document, type: 'edit' });
              }
              return startEdit(document, name ?? 'background', initialFormData);
            };

            return (
              <DocumentWrapper>
                <DocumentHeader
                  name={'background'}
                  headerId={
                    editing && documentType(documents, editing) === 'background'
                      ? 'background.addBasicInformationDocument'
                      : editing && documentType(documents, editing) === 'measurement'
                        ? 'background.addCareData'
                        : 'background.title'
                  }
                  editing={editing}
                  editButtons={
                    <DocumentCreationButton
                      name="background"
                      text={
                        openTab === 0
                          ? 'background.addCareData'
                          : openTab === 1
                            ? 'background.addBasicInformationDocument'
                            : 'general.new'
                      }
                      onClick={
                        openTab === 0
                          ? () => setMeasurementNewData({})
                          : openTab === 1
                            ? startEdit({}, 'background')
                            : undefined
                      }
                    />
                  }
                />
                <React.Fragment>
                  {editing && documentType(documents, editing) === 'background' && (
                    <BackgroundFormDynamic formData={formData} documents={backgroundDocuments} users={users} />
                  )}
                  {!editing && (
                    <HistoryTabs indexSelectionTools={{ index: openTab, changeFunction: index => setOpenTab(index) }}>
                      {[
                        {
                          title: 'background.careTable',
                          content: <CareTableHistory documents={careTableDocuments} myDocuments={myCareTableDocs} />,
                        },
                        ...(platform === 'ninmt'
                          ? []
                          : [
                              {
                                title: 'background.basicInformation',
                                content:
                                  sortedBackgroundDocuments.length > 0 && sortedBackgroundDocuments[0] ? (
                                    <React.Fragment>
                                      <HistoryRow
                                        headerText={
                                          sortedBackgroundDocuments[0].date
                                            ? formatPartialDate(sortedBackgroundDocuments[0].date)
                                            : undefined
                                        }
                                        key={sortedBackgroundDocuments[0]._id}
                                        rowButton={
                                          <HistoryRowControls
                                            document={sortedBackgroundDocuments[0]}
                                            startEdit={startEditFunction}
                                            enableButtons={isBackgroundDocumentShared(sortedBackgroundDocuments[0])}
                                            deleteHandler={
                                              isBackgroundDocumentShared(sortedBackgroundDocuments[0])
                                                ? () =>
                                                    handleMyBackgroundDelete(sortedBackgroundDocuments[0]?._id ?? '')
                                                : undefined
                                            }
                                          />
                                        }
                                        controlsMargin={false}
                                      >
                                        <HistoryRowData
                                          documents={sortedBackgroundDocuments}
                                          d={sortedBackgroundDocuments[0]}
                                          users={users}
                                        />
                                      </HistoryRow>
                                      {sortedBackgroundDocuments.length > 1 && (
                                        <CollapseElem
                                          localeIDs={{
                                            showMessage: 'background.showPastDocuments',
                                            hideMessage: 'background.hidePastDocuments',
                                          }}
                                          amount={sortedBackgroundDocuments.slice(1).length}
                                        >
                                          {sortedBackgroundDocuments.slice(1).map(d => (
                                            <HistoryRow
                                              headerText={'date' in d ? formatPartialDate(d.date) : undefined}
                                              key={d._id}
                                              rowButton={
                                                <HistoryRowControls
                                                  document={d}
                                                  startEdit={startEditFunction}
                                                  enableButtons={isBackgroundDocumentShared(d)}
                                                  deleteHandler={
                                                    isBackgroundDocumentShared(d)
                                                      ? () => handleMyBackgroundDelete(d._id)
                                                      : undefined
                                                  }
                                                />
                                              }
                                              controlsMargin={false}
                                            >
                                              <HistoryRowData
                                                documents={sortedBackgroundDocuments}
                                                d={d}
                                                users={users}
                                              />
                                            </HistoryRow>
                                          ))}
                                        </CollapseElem>
                                      )}
                                    </React.Fragment>
                                  ) : (
                                    <></>
                                  ),
                              },
                            ]),
                      ]}
                    </HistoryTabs>
                  )}
                </React.Fragment>
              </DocumentWrapper>
            );
          }}
        </FormEditingHandler>
      )}
    </React.Fragment>
  );
};

type DocTypes = DocTypesByPageName['background'];

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<DocTypes>>;
}

export default Background;
